import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';

import firebase from '../../../Firebase/Firebase';
import ProjectImageCarousel from '../../../Components/Media/ProjectImageCarousel';
import { subscribeToArtisanOffers, offerStatusLabel } from '../../../Services/demandOffers';
import { withdrawDemandOffer } from '../../../Store/Actions/actionOffer';
import { getProjectDescription, getProjectImages } from '../../../Utils/legacyData';
import EstimationWindow from './EstimationWindow';
import { useI18n } from '../../../i18n/I18nProvider';



const ArtisanOffersPanel = ({ artisanId, loading, withdrawOffer, onCountsChange }) => {
  const { t, pick, formatDate, formatCurrency } = useI18n();
  const filters = useMemo(() => ([
    { id: 'pending', label: t('jobs.offers.pending') },
    { id: 'accepted', label: t('jobs.offers.accepted') },
    { id: 'declined', label: t('jobs.offers.declined') },
    { id: 'all', label: t('jobs.offers.all') },
  ]), [t]);
  const [offers, setOffers] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('pending');
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [withdrawing, setWithdrawing] = useState('');

  useEffect(() => {
    setFetching(true);
    return subscribeToArtisanOffers(
      firebase.firestore(),
      artisanId,
      (rows) => {
        setOffers(rows);
        setFetching(false);
      },
      (subscriptionError) => {
        setError(subscriptionError?.message || t('jobs.offers.loadError'));
        setFetching(false);
      },
    );
  }, [artisanId, t]);

  const counts = useMemo(() => ({
    pending: offers.filter((offer) => offer.status === 'pending').length,
    accepted: offers.filter((offer) => offer.status === 'accepted').length,
    declined: offers.filter((offer) => offer.status === 'declined').length,
    withdrawn: offers.filter((offer) => offer.status === 'withdrawn').length,
    total: offers.length,
  }), [offers]);

  useEffect(() => onCountsChange?.(counts), [counts, onCountsChange]);

  const visibleOffers = useMemo(() => offers
    .filter((offer) => filter === 'all' || offer.status === filter)
    .sort((a, b) => {
      const time = (value) => value?.toMillis?.() || value?.seconds * 1000 || 0;
      return time(b.updated_at || b.created_at) - time(a.updated_at || a.created_at);
    }), [filter, offers]);

  const withdraw = async (offer) => {
    if (withdrawing) return;
    setWithdrawing(offer.demand_id);
    await withdrawOffer(offer.demand_id, artisanId);
    setWithdrawing('');
  };

  return (
    <Wrapper>
      <Intro>
        <div>
          <Eyebrow>{t('jobs.offers.eyebrow')}</Eyebrow>
          <h3>{t('jobs.offers.title')}</h3>
        </div>
        <Counters>
          <Counter $attention={counts.pending > 0}><strong>{counts.pending}</strong><span>{t('jobs.offers.pendingLower')}</span></Counter>
          <Counter><strong>{counts.accepted}</strong><span>{t('jobs.offers.acceptedLower')}</span></Counter>
          <Counter><strong>{counts.declined}</strong><span>{t('jobs.offers.declinedLower')}</span></Counter>
        </Counters>
      </Intro>

      <Filters>
        {filters.map((item) => (
          <FilterButton key={item.id} type="button" $active={filter === item.id} onClick={() => setFilter(item.id)}>
            {item.label}
            <strong>{item.id === 'all' ? counts.total : counts[item.id] || 0}</strong>
          </FilterButton>
        ))}
      </Filters>

      {fetching ? (
        <State>{t('jobs.offers.loading')}</State>
      ) : error ? (
        <ErrorState>{error}</ErrorState>
      ) : visibleOffers.length === 0 ? (
        <State>
          <SendOutlinedIcon />
          <strong>{t('jobs.offers.emptyTitle')}</strong>
          <span>{t('jobs.offers.emptyBody')}</span>
        </State>
      ) : (
        <Cards>
          {visibleOffers.map((offer) => {
            const demand = offer.demand || offer.demand_snapshot || {};
            const images = getProjectImages(demand).length
              ? getProjectImages(demand)
              : getProjectImages({ demand_image: offer.demand_snapshot?.demand_image || [] });
            const description = getProjectDescription(demand)
              || offer.demand_snapshot?.description
              || t('jobs.offers.projectWithoutDescription');
            const location = demand.public_location?.label
              || offer.demand_snapshot?.public_location?.label
              || demand.demand_address
              || offer.demand_snapshot?.demand_address
              || t('jobs.offers.locationMissing');
            const pending = offer.status === 'pending';
            const accepted = offer.status === 'accepted';
            const assessmentMode = offer.assessment_mode || 'visit_required';
            const moreInfo = assessmentMode === 'more_info';
            const clarificationResponse = String(offer.client_clarification_response || '').trim();

            return (
              <OfferCard key={`${offer.demand_id}:${offer.artisan_id}`}>
                <Gallery images={images} fallbackSrc="/images/project-placeholder.svg" alt={t('jobs.offers.projectAlt', { description })} />
                <Body>
                  <TopLine>
                    <Status $status={offer.status}>{offerStatusLabel(offer.status, t)}</Status>
                    <DateLabel><CalendarTodayOutlinedIcon /> {formatDate(offer.updated_at || offer.created_at, { dateStyle: 'short' })}</DateLabel>
                  </TopLine>
                  <h4>{description}</h4>
                  <Location><LocationOnOutlinedIcon /> {location}</Location>
                  <Proposal>{offer.estimation_description || t('common.noMessage')}</Proposal>

                  {moreInfo && (
                    <ClarificationBox $answered={Boolean(clarificationResponse)}>
                      <strong>{pick('Précisions demandées au client', 'Information requested from the client')}</strong>
                      <p>{offer.additional_info_request || pick('Votre demande de précisions a été envoyée.', 'Your clarification request has been sent.')}</p>
                      {clarificationResponse ? (
                        <ClientAnswer>
                          <span>{pick('Réponse du client', 'Client response')}</span>
                          <p>{clarificationResponse}</p>
                          <small>{pick('Vous pouvez maintenant modifier cette réponse en devis à distance, visite ou nouvelle demande de précisions.', 'You can now update this response into a remote quote, a visit request, or another clarification request.')}</small>
                        </ClientAnswer>
                      ) : (
                        <WaitingAnswer>{pick('En attente de la réponse du client. Le projet reste ouvert aux autres artisans.', 'Waiting for the client response. The project remains open to other artisans.')}</WaitingAnswer>
                      )}
                    </ClarificationBox>
                  )}

                  <Meta>
                    {assessmentMode === 'visit_required' && offer.visit_date ? (
                      <div><span>{t('jobs.offers.proposedVisit')}</span><strong>{formatDate(offer.visit_date, { dateStyle: 'short' })}</strong></div>
                    ) : null}
                    <div><span>{t('common.client')}</span><strong>{[demand.user_firstname || offer.demand_snapshot?.client_firstname, demand.user_lastname || offer.demand_snapshot?.client_lastname].filter(Boolean).join(' ') || t('common.client')}</strong></div>
                    {Number(offer.estimated_budget) > 0 ? (
                      <div><span>{t('jobs.offers.estimatedBudget')}</span><strong>{formatCurrency(offer.estimated_budget)}</strong><small>{t('jobs.offers.budgetHint')}</small></div>
                    ) : null}
                  </Meta>

                  {accepted && <Accepted><CheckCircleOutlineOutlinedIcon /> {t('jobs.offers.becameJob')}</Accepted>}

                  {pending && (
                    <Actions>
                      <SecondaryButton type="button" onClick={() => setSelectedOffer(offer)} disabled={loading || Boolean(withdrawing)}>
                        <EditOutlinedIcon /> {moreInfo && clarificationResponse
                          ? pick('Préparer ma proposition', 'Prepare my proposal')
                          : t('jobs.offers.edit')}
                      </SecondaryButton>
                      <DangerButton type="button" onClick={() => withdraw(offer)} disabled={loading || Boolean(withdrawing)}>
                        <UndoOutlinedIcon /> {withdrawing === offer.demand_id ? t('jobs.offers.withdrawing') : t('jobs.offers.withdraw')}
                      </DangerButton>
                    </Actions>
                  )}
                </Body>
              </OfferCard>
            );
          })}
        </Cards>
      )}

      <EstimationWindow
        showModal={selectedOffer ? 'open' : 'close'}
        handleClick={() => setSelectedOffer(null)}
        demandId={selectedOffer?.demand_id}
        initialOffer={selectedOffer}
      />
    </Wrapper>
  );
};

const Wrapper = styled.section`display:grid;gap:1rem;`;
const Intro = styled.div`display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;padding:1rem;border:1px solid #e7dfd2;border-radius:1rem;background:#fff;h3{margin:.18rem 0;color:#111111;font-size:1.18rem;}p{max-width:42rem;margin:.35rem 0 0;color:#726b61;line-height:1.55;}@media(max-width:50rem){flex-direction:column;}`;
const Eyebrow = styled.span`color:#a88a5d;font-size:.7rem;font-weight:900;letter-spacing:.09em;text-transform:uppercase;`;
const Counters = styled.div`display:flex;gap:.55rem;flex-wrap:wrap;`;
const Counter = styled.div`min-width:5.2rem;display:grid;justify-items:center;padding:.6rem .75rem;border-radius:.8rem;background:${({$attention})=>$attention?'#efe4cf':'#f1f5f7'};strong{color:#111111;font-size:1.2rem;}span{color:#726b61;font-size:.65rem;font-weight:850;text-transform:uppercase;}`;
const Filters = styled.div`display:flex;gap:.45rem;overflow:auto;padding-bottom:.1rem;`;
const FilterButton = styled.button`display:flex;align-items:center;gap:.4rem;min-height:2.55rem;padding:.55rem .75rem;border:1px solid ${({$active})=>$active?'#111111':'#e7dfd2'};border-radius:999px;background:${({$active})=>$active?'#111111':'#fff'};color:${({$active})=>$active?'#fff':'#5f5a52'};font:inherit;font-size:.78rem;font-weight:850;white-space:nowrap;cursor:pointer;strong{min-width:1.25rem;padding:.1rem .28rem;border-radius:999px;background:${({$active})=>$active?'rgba(255,255,255,.18)':'#f1ece5'};}`;
const Cards = styled.div`display:grid;gap:1rem;`;
const OfferCard = styled.article`display:grid;grid-template-columns:minmax(15rem,19rem) minmax(0,1fr);overflow:hidden;border:1px solid #e7dfd2;border-radius:1.1rem;background:#fff;box-shadow:0 .75rem 2rem rgba(0,0,0,.07);@media(max-width:48rem){grid-template-columns:1fr;}`;
const Gallery = styled(ProjectImageCarousel)`height:100%;min-height:17rem;border-radius:0;`;
const Body = styled.div`display:grid;align-content:start;gap:.8rem;padding:1rem;h4{margin:0;color:#111111;font-size:1.05rem;line-height:1.4;}`;
const TopLine = styled.div`display:flex;align-items:center;justify-content:space-between;gap:.65rem;`;
const Status = styled.span`padding:.36rem .62rem;border-radius:999px;background:${({$status})=>$status==='accepted'?'#e5f7ed':$status==='declined'?'#eef1f3':$status==='withdrawn'?'#fff0ee':'#efe4cf'};color:${({$status})=>$status==='accepted'?'#1d6844':$status==='declined'?'#64737d':$status==='withdrawn'?'#8a3d35':'#8a6f48'};font-size:.7rem;font-weight:850;`;
const DateLabel = styled.span`display:flex;align-items:center;gap:.3rem;color:#726b61;font-size:.7rem;svg{font-size:.9rem;}`;
const Location = styled.div`display:flex;align-items:flex-start;gap:.35rem;color:#5f5a52;font-size:.8rem;svg{flex:0 0 auto;font-size:1rem;}`;
const Proposal = styled.p`margin:0;padding:.75rem;border-radius:.75rem;background:#faf8f4;color:#111111;line-height:1.55;white-space:pre-wrap;`;
const ClarificationBox = styled.section`display:grid;gap:.55rem;padding:.8rem;border:1px solid ${({$answered})=>$answered?'#b8dbc5':'#ead29b'};border-radius:.8rem;background:${({$answered})=>$answered?'#f1faf4':'#fffaf0'};color:#111;>strong{font-size:.82rem;} >p{margin:0;color:#66583f;line-height:1.5;white-space:pre-wrap;}`;
const ClientAnswer = styled.div`display:grid;gap:.32rem;padding:.7rem;border-radius:.7rem;background:#fff;span{color:#17623a;font-size:.68rem;font-weight:900;text-transform:uppercase;}p{margin:0;color:#1f2f28;line-height:1.55;white-space:pre-wrap;}small{color:#617068;line-height:1.45;}`;
const WaitingAnswer = styled.div`padding:.65rem;border-radius:.7rem;background:#fff;color:#73510a;font-size:.76rem;line-height:1.45;`;
const Meta = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.55rem;div{display:grid;padding:.65rem;border-radius:.7rem;background:#faf8f4;}span{color:#726b61;font-size:.65rem;font-weight:800;text-transform:uppercase;}strong{color:#111111;font-size:.8rem;}small{color:#726b61;font-size:.68rem;margin-top:.15rem;}@media(max-width:32rem){grid-template-columns:1fr;}`;
const Actions = styled.div`display:flex;flex-wrap:wrap;gap:.55rem;`;
const BaseButton = styled.button`min-height:2.55rem;display:flex;align-items:center;justify-content:center;gap:.38rem;padding:.58rem .78rem;border-radius:.7rem;font:inherit;font-size:.78rem;font-weight:850;cursor:pointer;&:disabled{opacity:.55;cursor:wait;}svg{font-size:1rem;}`;
const SecondaryButton = styled(BaseButton)`border:1px solid #e7dfd2;background:#fff;color:#111111;`;
const DangerButton = styled(BaseButton)`border:1px solid #eccdca;background:#fff7f6;color:#8a3c35;`;
const Accepted = styled.div`display:flex;align-items:center;gap:.4rem;padding:.7rem;border-radius:.75rem;background:#eef9f3;color:#216a46;font-size:.8rem;font-weight:850;`;
const State = styled.div`min-height:12rem;display:grid;place-items:center;align-content:center;gap:.45rem;padding:1.5rem;border:1px dashed #ddd3c4;border-radius:1rem;background:#fff;color:#726b61;text-align:center;svg{font-size:2rem;color:#9a711f;}`;
const ErrorState = styled(State)`border-color:#ecc8c4;background:#fff7f6;color:#8b352f;`;

const mapStateToProps = ({ demands }) => ({ loading: Boolean(demands?.loading) });
const mapDispatchToProps = (dispatch) => ({
  withdrawOffer: (demandId, artisanId) => dispatch(withdrawDemandOffer(demandId, artisanId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ArtisanOffersPanel);
